import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {AuditCategory, AuditStructure, RelatedAction} from '../api/digitalCertificateApi';
import {AppLanguage} from '../types/AppLanguage';
import {getAuditFieldById} from './getAuditFieldById';
import {getRelatedActionByComparisonKey} from './getRelatedActionByComparisonKey';
import {isCategoryEnabledInAudit} from './isCategoryEnabledInAudit';

type ServiceBookStateParams = {
  relatedActions: RelatedAction[];
  auditCategories: AuditCategory[];
  language: AppLanguage;
  /**
   * Parent the SERVICE_BOOK category is nested under in the audit structure. Callers pass their own
   * section key, so the service book renders only in the section the BE currently nests it under.
   */
  parentUniqueKey: string;
  auditStructure: AuditStructure;
};

/**
 * T20-125652 moves the service book from Documentation to Service History. The move happens on the
 * BE, so both sections read the service book through this util - Documentation with
 * VEHICLE_DOCUMENTATION, Service History with SERVICE_HISTORY. Only the section matching the
 * current structure gets `isEnabledInAudit: true`, which keeps old and new BE structures
 * compatible without a feature flag. Once every audit is migrated, the Documentation caller can go.
 */
export const getServiceBookState = (params: ServiceBookStateParams) => {
  const serviceBookTypeRelatedAction = getRelatedActionByComparisonKey(
    'documentationServiceBookType',
    params.relatedActions
  );

  const isEnabledInAudit = isCategoryEnabledInAudit({
    categories: params.auditStructure?.categories ?? [],
    parentUniqueKey: params.parentUniqueKey,
    uniqueKey: 'SERVICE_BOOK',
  });

  if (isNil(serviceBookTypeRelatedAction)) {
    return {isEnabledInAudit};
  }

  const selectedTypeValue = getAuditFieldById(
    serviceBookTypeRelatedAction.id,
    params.auditCategories
  )?.value;

  const typeContent = serviceBookTypeRelatedAction.values?.find(
    (v) => v.value === selectedTypeValue
  )?.content;

  const serviceBookStateRelatedAction = typeContent?.relatedActions?.[0] ?? null;

  if (isNil(serviceBookStateRelatedAction)) {
    return {isEnabledInAudit};
  }

  const stateAuditFieldValue = getAuditFieldById(
    serviceBookStateRelatedAction.id,
    params.auditCategories
  )?.value;

  const stateLabel =
    serviceBookStateRelatedAction.values?.find((v) => v.value === stateAuditFieldValue)?.label?.[
      params.language
    ] ?? null;

  const commentRelatedAction = typeContent?.additionalInformation?.find(
    (additionalInformation) => additionalInformation.type === 'additional_comment'
  );

  const commentValue = getAuditFieldById(commentRelatedAction?.id, params.auditCategories)?.value;

  const comment = isNotNilOrEmpty(commentValue) && commentValue !== 'null' ? commentValue : null;

  return {
    value: stateLabel,
    comment,
    isEnabledInAudit,
  };
};
